import React from "react";
import DOMPurify from "dompurify";
import styled from "styled-components";

import { combineClassNames } from "@utils/appUtils";

import { colors } from "@common/styles/Theme";

import { TypographyMapping } from "./WPTypography.constants";

// The Typography is based on the typography guidelines
/**
 * Typography component for rendering text with various styles and alignments.
 *
 * @param {Object} props - The properties object.
 * @param {string} props.variant - The variant of the typography element (e.g., 'body', 'cardHeader').
 * @param {string} [props.text] - The text content to be displayed. If not provided, children will be used.
 * @param {React.ReactNode} [props.children] - The children elements to be rendered if text is not provided.
 * @param {string} [props.className] - Additional class names to apply to the element.
 * @param {Object} [props.style] - Inline styles to apply to the element.
 * @param {string} [props.align] - Text alignment class (e.g., 'left', 'center', 'right').
 *
 * @returns {JSX.Element} The rendered typography element.
 */

const Typography = React.forwardRef(
  ({ id = null, variant, text = "", children, className = "", style = {}, asElement, renderHtml = false }, ref) => {
    const { element = "p", className: defaultClassName = "" } = TypographyMapping[variant] || {};
    const Element = asElement || element;

    const textValue = text || children;

    const elementProps = { id, className: combineClassNames(defaultClassName, className), style, ref };

    DOMPurify.addHook("afterSanitizeAttributes", function (node) {
      if (node.tagName === "A") {
        node.setAttribute("target", "_blank");
      }
    });

    if (renderHtml && typeof textValue === "string") {
      return <Element {...elementProps} dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(textValue) }}></Element>;
    }
    return <Element {...elementProps}>{textValue}</Element>;
  },
);

Typography.displayName = "Typography";

// default font family is notosans
const WPTypogrpahy = styled(Typography)`
  margin: 0px;
  color: ${props => props.color || colors.Black};
  font-weight: ${props => props.fontWeight ?? 400};
  font-family: ${props => props.fontFamily};
  font-size: ${props => `${props.fontSize}px`};
  ${props => props.align && `text-align: ${props.align};`}

  &.body {
    font-size: 14px;
  }

  &.cardHeader {
    font-family: Diodrum;
    font-size: 16px;
    font-weight: 600;
  }

  &.xs-Text {
    font-family: Diodrum;
    font-size: 10px;
  }

  &.reportSectionHeader {
    font-size: 14px;
    font-weight: 700;
    line-height: 22px;
  }

  &.tableData {
    font-size: 14px;
    word-break: break-word;
    white-space: pre-line;
  }

  &.tableColumnData {
    font-family: Diodrum;
    font-size: 14px;
    font-weight: 600;
    word-break: break-word;
  }

  sup {
    font-size: 0.75em;
    vertical-align: middle;
  }

  sub {
    font-size: 0.75em;
    vertical-align: sub;
  }
`;

export default WPTypogrpahy;
